import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Home } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";

export default function PaymentSuccess({ profile, refreshProfile }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | paid | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          if (profile) await refreshProfile(profile.id);
          celebrate();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep polling a few times
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        setState("error");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-shell dotted-bg flex items-center justify-center p-6">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className="bg-white rounded-[2.5rem] border-4 border-slate-900/10 p-10 w-full max-w-sm text-center"
        style={{ boxShadow: "0 14px 0 0 rgba(37,99,235,0.35)" }}
        data-testid="payment-success-page"
      >
        {state === "checking" && (
          <>
            <Loader2 size={56} strokeWidth={3} className="animate-spin text-blue-500 mx-auto mb-5" />
            <h1 className="font-fredoka text-3xl font-bold text-slate-900">Confirming payment…</h1>
            <p className="text-lg font-bold text-slate-400 mt-2">Just a moment!</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="text-6xl mb-3">🎉</div>
            <h1 className="font-fredoka text-4xl font-bold text-slate-900 flex items-center justify-center gap-2">
              <Sparkles size={30} strokeWidth={3} className="text-yellow-500" /> Premium!
            </h1>
            <p className="text-lg font-bold text-slate-500 mt-2 mb-8">
              Every learning adventure is unlocked. Have fun!
            </p>
            <button
              data-testid="success-home-button"
              onClick={() => navigate("/home")}
              className="toy-btn w-full flex items-center justify-center gap-2 bg-green-500 text-white font-fredoka text-2xl font-bold py-4 rounded-2xl"
              style={{ boxShadow: "0 6px 0 0 rgba(21,128,61,1)" }}
            >
              <Home size={26} strokeWidth={3} /> Start Playing
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="text-6xl mb-3">😕</div>
            <h1 className="font-fredoka text-3xl font-bold text-slate-900">Hmm, that didn't finish</h1>
            <p className="text-lg font-bold text-slate-400 mt-2 mb-8">
              No charge was confirmed. You can try again anytime.
            </p>
            <button
              data-testid="error-home-button"
              onClick={() => navigate("/home")}
              className="toy-btn w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-700 font-fredoka text-2xl font-bold py-4 rounded-2xl"
              style={{ boxShadow: "0 6px 0 0 rgba(15,23,42,0.12)" }}
            >
              <Home size={26} strokeWidth={3} /> Back Home
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
